import React, { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { motion } from "framer-motion"
import { toast } from "sonner"
import { BuildRounded, CalendarMonth, AccessTime, Edit, Person } from "@mui/icons-material"
import { MuiButton, MuiCheckbox } from "@ace/ace-ui-web"
import { Typography } from "@mui/material"
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchSkills, Skills } from '../../redux/SkillReducer/skillSlice';
import { fetchEligibility, WorkerEligibility } from '../../redux/EligibilityReducer/eligibilitySlice';
import { fetchSiteDetails } from '../../redux/SiteDetailsReducer/siteDetailsSlice';
import { createShiftRequest } from '../../redux/CreateShiftReducer/createShiftRequestSlice';
import { Teams, fetchSkills as fetchTeams } from "../../redux/TeamReducer/teamSlice";
import { fetchShiftTimeOptions, ShiftTimeOption } from '../../redux/ShiftTimeReducer/shiftTimeSlice';
import { validateNotificationTime, validateShiftStartTime, getShortTimeZoneLabelForDate } from '../../utils/timezoneUtils';
import { isDirectReportShiftType, resolveRequestType } from '../../utils/shiftTypeUtils';
import { useUserRole } from "../../hooks/useUserRole";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { SelectorField, DateTimeField, FormField } from "./FormField";
import { ShiftDetailsDisplay } from "./ShiftDetailsDisplay";
import { ActivityLogEventActions } from "../../constants/activityLogEventActions"
import styles from "./postShift.module.css"

// Time conversion utilities
const to24Hour = (timeStr: string | null | undefined): string => {
    if (!timeStr) return "";
    const parts = timeStr.trim().split(/\s+/);
    if (parts.length < 2) return "";
    const [time, period] = parts;
    const [hStr, mStr] = time.split(":");
    let h = Number.parseInt(hStr, 10);
    const m = Number.parseInt(mStr, 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return "";
    const p = period.toUpperCase();
    if (p === "PM" && h !== 12) h += 12;
    if (p === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const stripSeconds = (s: string | null | undefined): string => {
    if (!s) return "";
    return s.replace(/(\d{1,2}):(\d{2}):\d{2}\s*(AM|PM)/i, "$1:$2 $3");
};

const formatDate = (dateStr: string): string => {
    if (!dateStr) return "";
    return new Date(dateStr + "T00:00:00").toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

interface ConfirmationExtrasProps {
    directReportOnly: boolean;
    notes: string;
}

interface PostShiftPageProps {
    setFooterContent?: (content: React.ReactNode | null) => void;
}

const ConfirmationExtras: React.FC<ConfirmationExtrasProps> = ({ directReportOnly, notes }) => (
    <>
        {directReportOnly && (
            <div className={styles.confirmSection}>
                <p className={styles.confirmLabel} style={{ fontWeight: 'bold', color: 'var(--color-primary-default)' }}>For Direct Reports Only</p>
            </div>
        )}
        {notes && (
            <div className={styles.confirmSection}>
                <p className={styles.confirmLabel}>Notes</p>
                <p className={styles.confirmValue}>{notes}</p>
            </div>
        )}
    </>
);

const PostShiftPage: React.FC<PostShiftPageProps> = ({ setFooterContent }) => {
    const dispatch = useAppDispatch();
    const { user: currentUser } = useAppSelector((state) => state.currentUser);
    const { skills, loading: skillsLoading } = useAppSelector((state) => state.skills);
    const { eligibility, loading: eligibilityLoading } = useAppSelector((state) => state.eligibility);
    const { siteDetails } = useAppSelector((state) => state.siteDetails);
    const { teams, loading: teamsLoading } = useAppSelector((state) => state.teams);
    const { options: shiftTimeOptions, loading: shiftTimeOptionsLoading } = useAppSelector((state) => state.shiftTime);
    const { isHROrManager } = useUserRole();

    const shiftTimeOptionsFetched = useRef(false);
    const timeZone = siteDetails?.timeZoneId;
    const timeZoneShort = getShortTimeZoneLabelForDate(timeZone);

    // Form state
    const [formData, setFormData] = useState({
        skill: "",
        shiftType: "",
        team: [] as string[],
        numberOfShifts: 1,
        date: "",
        startTime: "",
        endTime: "",
        directReportOnly: false,
        notes: "",
    });

    const [scheduleData, setScheduleData] = useState({
        notificationDate: "",
        notificationTime: "",
    });

    const [selectedShiftTimeOption, setSelectedShiftTimeOption] = useState<string>("");
    const [isStartTimeEditable, setIsStartTimeEditable] = useState(true);
    const [isEndTimeEditable, setIsEndTimeEditable] = useState(true);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Dialog state
    const [showScheduleDialog, setShowScheduleDialog] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showScheduleConfirmDialog, setShowScheduleConfirmDialog] = useState(false);

    // Computed options
    const skillOptions = useMemo(() =>
        skills?.map((skill: Skills) => ({
            label: skill.lookupValueDesc,
            value: skill.lookupValueCd
        })) || [], [skills]);

    const eligibilityOptions = useMemo(() =>
        eligibility
            ?.filter((option: WorkerEligibility) => !isDirectReportShiftType(option.lookupValueCd))
            .map((option: WorkerEligibility) => ({
                label: option.lookupValueDesc,
                value: option.lookupValueCd
            })) || [], [eligibility]);

    const filteredEligibilityOptions = isHROrManager
        ? eligibilityOptions.filter((option) => option.label.toLowerCase() !== 'worker')
        : eligibilityOptions.filter((option) => option.label.toLowerCase() === 'worker');

    const filteredSkillOptions = isHROrManager
        ? skillOptions
        : skillOptions.filter((option) => currentUser?.skills?.includes(option.label));

    const teamOptions = useMemo(() => Array.isArray(teams)
        ? teams.map((teamItem: Teams) => ({
            label: teamItem.lookupValueDesc,
            value: teamItem.lookupValueCd
        }))
        : [], [teams]);

    const shiftTimeSelectOptions = (shiftTimeOptions || []).map((opt: ShiftTimeOption, idx: number) => ({
        label: `${stripSeconds(opt.startTimeFormatted)} - ${stripSeconds(opt.endTimeFormatted)}`,
        value: `${opt.startTimeFormatted ?? ""}|${opt.endTimeFormatted ?? ""}|${idx}`,
    }));

    // Effects
    useEffect(() => {
        if (currentUser) {
            dispatch(fetchSkills(currentUser.siteCd));
            dispatch(fetchEligibility(currentUser.siteCd));
            if (isHROrManager) {
                dispatch(fetchTeams());
            }
            dispatch(fetchSiteDetails(currentUser.siteCd));
        }
    }, [dispatch, currentUser, isHROrManager]);

    // Handlers
    const handleShiftTimeOptionsOpen = useCallback(() => {
        if (shiftTimeOptionsFetched.current) return;
        if (!currentUser?.siteCd) {
            toast.error('Site information is not available');
            return;
        }
        shiftTimeOptionsFetched.current = true;
        dispatch(fetchShiftTimeOptions(currentUser.siteCd));
    }, [dispatch, currentUser]);

    const handleShiftTimeOptionChange: (value: string | string[]) => void = useCallback((value) => {
        if (Array.isArray(value)) return;
        setSelectedShiftTimeOption(value);
        const [startFormatted, endFormatted] = value.split("|");
        const start = to24Hour(startFormatted);
        const end = to24Hour(endFormatted);
        setFormData(prev => ({ ...prev, startTime: start, endTime: end }));
        setErrors(prev => {
            const next = { ...prev };
            delete next.startTime;
            delete next.endTime;
            delete next.shiftDateTime;
            delete next.startEndTime;
            return next;
        });
    }, []);

    const clearError = useCallback((field: string) => {
        setErrors(prev => {
            const next = { ...prev };
            delete next[field];
            return next;
        });
    }, []);

    const updateFormField = useCallback((field: string, value: any, clearFields: string[] = []) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        [field, ...clearFields].forEach(clearError);
    }, [clearError]);

    const validatePostForm = useCallback(() => {
        const newErrors: Record<string, string> = {};

        if (!formData.skill) newErrors.skill = "Skill is required";
        if (!formData.shiftType) newErrors.shiftType = "Shift type is required";
        if (isHROrManager && formData.team.length === 0) newErrors.team = "At least one team is required";

        if (!formData.numberOfShifts) {
            newErrors.numberOfShifts = "Number of shifts is required";
        } else if (formData.numberOfShifts < 1) {
            newErrors.numberOfShifts = "Number of shifts must be at least 1";
        } else if (formData.numberOfShifts > 100) {
            newErrors.numberOfShifts = "Number of shifts cannot exceed 100";
        }

        if (!formData.date) newErrors.date = "Date is required";
        if (!formData.startTime) newErrors.startTime = "Start time is required";
        if (!formData.endTime) newErrors.endTime = "End time is required";

        if (formData.date && formData.startTime && timeZone) {
            const validation = validateShiftStartTime(formData.date, formData.startTime, timeZone);
            if (!validation.isValid) {
                newErrors.shiftDateTime = validation.error || "Invalid shift time";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData, timeZone, isHROrManager]);

    const validateScheduleForm = useCallback(() => {
        const newErrors: Record<string, string> = {};

        if (!scheduleData.notificationDate) {
            newErrors.notificationDate = "Notification date is required";
        }
        if (!scheduleData.notificationTime) {
            newErrors.notificationTime = "Notification time is required";
        }

        if (scheduleData.notificationDate && scheduleData.notificationTime && timeZone) {
            const validation = validateNotificationTime(
                scheduleData.notificationDate,
                scheduleData.notificationTime,
                formData.date,
                formData.startTime,
                timeZone
            );

            if (!validation.isValid) {
                if (validation.error?.includes("before current time")) {
                    newErrors.notificationTime = validation.error;
                } else if (validation.error?.includes("after the shift starts")) {
                    newErrors.notificationDateTime = validation.error;
                } else {
                    newErrors.notificationTime = validation.error || "Invalid notification time";
                }
            }
        } else if (!timeZone) {
            newErrors.notificationTime = "Site timezone information is not available. Please try again.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [scheduleData, formData, timeZone]);

    const createShiftPayload = useCallback((scheduled: boolean) => ({
        skills: formData.skill ? [formData.skill] : [],
        team: isHROrManager && formData.team.length > 0 ? [...formData.team].sort((a, b) => a.localeCompare(b)).join(',') : "",
        postDate: new Date().toISOString(),
        postById: currentUser?.workdayId || "",
        postByName: `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim(),
        requestDate: formData.date,
        startTime: formData.startTime,
        endTime: formData.endTime,
        requestType: resolveRequestType(formData.shiftType, formData.directReportOnly),
        requestSite: currentUser?.siteCd || "",
        requestShiftId: isHROrManager && formData.team.length > 0 ? [...formData.team].sort((a, b) => a.localeCompare(b)).join(',') : "",
        requestEmplCount: formData.numberOfShifts.toString(),
        requestNotes: formData.notes,
        requestNotifyDate: scheduled ? scheduleData.notificationDate : null,
        requestNotifyTime: scheduled ? scheduleData.notificationTime : null,
        scheduleNotificationFl: scheduled,
        eventAction: [ActivityLogEventActions.ShiftCreated]
    }), [formData, scheduleData, currentUser, isHROrManager]);

    const resetForm = useCallback(() => {
        setFormData({
            skill: "",
            shiftType: "",
            team: [],
            numberOfShifts: 1,
            date: "",
            startTime: "",
            endTime: "",
            directReportOnly: false,
            notes: "",
        });
        setScheduleData({
            notificationDate: "",
            notificationTime: "",
        });
        setErrors({});
        setIsStartTimeEditable(true);
        setIsEndTimeEditable(true);
        setSelectedShiftTimeOption("");
    }, []);

    const submitShift = useCallback(async (scheduled: boolean) => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            await dispatch(createShiftRequest({
                payload: createShiftPayload(scheduled)
            })).unwrap();

            const message = scheduled
                ? `Notification will be sent on ${scheduleData.notificationDate} at ${scheduleData.notificationTime}`
                : `${formData.skill} shift has been posted`;

            toast.success(scheduled ? "Shift notification scheduled!" : "Shift posted successfully!", {
                description: message
            });

            setShowConfirmDialog(false);
            setShowScheduleConfirmDialog(false);
            resetForm();
        } catch (err) {
            console.error("Shift submission error:", err);
            toast.error("Failed to process shift. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    }, [dispatch, createShiftPayload, isSubmitting, formData, scheduleData, resetForm]);

    const handlePostNowClick = useCallback(() => {
        if (!validatePostForm()) {
            toast.error("Please fill in all required shift details first");
            return;
        }
        setShowConfirmDialog(true);
    }, [validatePostForm]);

    const handleScheduleClick = useCallback(() => {
        if (!validatePostForm()) {
            toast.error("Please fill in all required shift details first");
            return;
        }
        setShowScheduleDialog(true);
    }, [validatePostForm]);

    const handleScheduleSubmit = useCallback(() => {
        if (!validateScheduleForm()) {
            toast.error("Please fill in all required schedule details first");
            return;
        }
        setShowScheduleDialog(false);
        setShowScheduleConfirmDialog(true);
    }, [validateScheduleForm]);

    const getMinDate = useCallback((): string => {
        const fallback = new Date().toISOString().split("T")[0];
        if (!timeZone) return fallback;
        try {
            return new Intl.DateTimeFormat('en-CA', { timeZone }).format(new Date());
        } catch {
            return fallback;
        }
    }, [timeZone]);

    // Shift details for confirmation dialogs
    const getShiftDetails = useCallback((includeSchedule: boolean = false) => {
        const details = [
            { label: "Skill", value: skillOptions.find(s => s.value === formData.skill)?.label },
            { label: "Shift Type", value: eligibilityOptions.find(e => e.value === formData.shiftType)?.label },
            ...(isHROrManager ? [{ label: "Team", value: formData.team.map(t => teamOptions.find(opt => opt.value === t)?.label || t).join(', ') }] : []),
            { label: "Number of Shifts", value: formData.numberOfShifts },
            { label: "Date", value: formatDate(formData.date) },
            { label: "Time", value: `${formData.startTime} - ${formData.endTime}` },
        ];

        if (includeSchedule) {
            details.push({
                label: "Notification will be sent on",
                value: `${formatDate(scheduleData.notificationDate)} at ${scheduleData.notificationTime}`
            });
        }

        return details;
    }, [formData, scheduleData, skillOptions, eligibilityOptions, isHROrManager, teamOptions]);

    const footerActions = useMemo(() => (
        <div className={styles.buttonGroup}>
            <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }} className={styles.buttonFull}>
                <MuiButton size="large" sx={{ width: '100%' }} onClick={handlePostNowClick}>
                    Post Shift Now
                </MuiButton>
            </motion.div>
            <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }} className={styles.buttonFull}>
                <MuiButton variant="outlined" size="large" onClick={handleScheduleClick} sx={{ width: '100%' }}>
                    Schedule for Later
                </MuiButton>
            </motion.div>
        </div>
    ), [handlePostNowClick, handleScheduleClick]);

    useEffect(() => {
        if (!setFooterContent) return;
        setFooterContent(footerActions);
        return () => setFooterContent(null);
    }, [setFooterContent, footerActions]);

    return (
        <div className={styles.drawerContainer}>
            <div className={`${styles.contentWrapper} ${styles.spaceY5}`}>
                <div className={styles.detailsTitle}>
                    <Typography variant="h6" component="h2" className="card-title-text">
                        Shift Details
                    </Typography>
                </div>

                {/* Form Fields */}
                <div className={styles.formGridCols2}>
                    <SelectorField
                        label="Skill"
                        icon={<BuildRounded sx={{ height: 1, fontSize: 15, transform: 'scaleX(-1)', color: "gray" }} />}
                        placeholder="Select a skill"
                        value={formData.skill}
                        onChange={(val) => updateFormField('skill', val)}
                        options={filteredSkillOptions}
                        disabled={skillsLoading}
                        error={errors.skill}
                    />

                    <SelectorField
                        label="Shift Type"
                        placeholder="Select a shift type"
                        value={formData.shiftType}
                        onChange={(val) => updateFormField('shiftType', val)}
                        options={filteredEligibilityOptions}
                        disabled={eligibilityLoading}
                        error={errors.shiftType}
                    />
                </div>

                <div className={styles.formGridCols2}>
                    {isHROrManager && (
                        <SelectorField
                            label="Team"
                            placeholder="Select teams"
                            value={formData.team}
                            onChange={(val) => updateFormField('team', val as string[])}
                            options={teamOptions}
                            disabled={teamsLoading}
                            error={errors.team}
                            multipleSelect={true}
                        />
                    )}

                    <div className={[styles.formGroup, 'form-group'].join(" ")}>
                        <label className={styles.formLabel} htmlFor="numberOfShifts">
                            Number of Shifts
                        </label>
                        <input
                            id="numberOfShifts"
                            max="100"
                            min="1"
                            type="number"
                            value={formData.numberOfShifts}
                            onChange={(e) => updateFormField('numberOfShifts', Math.max(1, Number.parseInt(e.target.value)))}
                            required
                        />
                        {errors.numberOfShifts && (
                            <p className={styles.errorMessage}>{errors.numberOfShifts}</p>
                        )}
                    </div>
                </div>

                <div className={styles.formGridCols2}>
                    <DateTimeField
                        label="Date"
                        icon={<CalendarMonth sx={{ height: 1, fontSize: 15, color: "gray" }} />}
                        type="date"
                        value={formData.date}
                        onChange={(e) => updateFormField('date', e.target.value, ['shiftDateTime'])}
                        min={getMinDate()}
                        error={errors.date}
                    />

                    <SelectorField
                        label="Shift Time"
                        icon={<AccessTime sx={{ height: 1, fontSize: 15, color: "gray" }} />}
                        placeholder={shiftTimeOptionsLoading ? "Loading..." : "Select a shift time (optional)"}
                        value={selectedShiftTimeOption}
                        onChange={handleShiftTimeOptionChange}
                        options={shiftTimeSelectOptions}
                        disabled={shiftTimeOptionsLoading}
                        onMouseDown={handleShiftTimeOptionsOpen}  
                    />
                </div>

                <div className={styles.formGridCols2}>
                    <DateTimeField
                        label={`Start Time (${timeZoneShort})`}
                        icon={<AccessTime sx={{ height: 1, fontSize: 15, color: "gray" }} />}
                        type="time"
                        value={formData.startTime}
                        onChange={(e) => updateFormField('startTime', e.target.value, ['shiftDateTime', 'startEndTime'])}
                        disabled={!isStartTimeEditable}
                        error={errors.startTime}
                        editButton={
                            <button
                                type="button"
                                className={styles.editButton}
                                onClick={() => setIsStartTimeEditable(!isStartTimeEditable)}
                            >
                                <Edit sx={{ height: 1, fontSize: 14 }} />
                            </button>
                        }
                    />

                    <DateTimeField
                        label={`End Time (${timeZoneShort})`}
                        icon={<AccessTime sx={{ height: 1, fontSize: 15, color: "gray" }} />}
                        type="time"
                        value={formData.endTime}
                        onChange={(e) => updateFormField('endTime', e.target.value, ['shiftDateTime', 'startEndTime'])}
                        disabled={!isEndTimeEditable}
                        error={errors.endTime}
                        editButton={
                            <button
                                type="button"
                                className={styles.editButton}
                                onClick={() => setIsEndTimeEditable(!isEndTimeEditable)}
                            >
                                <Edit sx={{ height: 1, fontSize: 14 }} />
                            </button>
                        }
                    />
                </div>

                {(errors.shiftDateTime || errors.startEndTime) && (
                    <p className={styles.errorMessage}>{errors.shiftDateTime || errors.startEndTime}</p>
                )}

                {/* Direct Report - only visible to Administrators/Supervisors */}
                {isHROrManager && (
                    <div className={styles.directReportRow}>
                        <Person sx={{ fontSize: 18, color: "gray" }} />
                        <p className={styles.formLabel}>Direct Reports Only?</p>
                        <MuiCheckbox
                            color="secondary"
                            checked={formData.directReportOnly}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => updateFormField('directReportOnly', event.target.checked)}
                        />
                    </div>
                )}

                {/* Notes */}
                <FormField label="Notes (Optional)" controlId="notes">
                    <textarea
                        id="notes"
                        placeholder="Add any important notes for employees..."
                        value={formData.notes}
                        maxLength={500}
                        onChange={(e) => updateFormField('notes', e.target.value)}
                        rows={3}
                        className={styles.textarea}
                    />
                    <p className={styles.charCounter}>{formData.notes.length} / 500</p>
                </FormField>

                </div>

            {/* Schedule Dialog */}
            <ConfirmationDialog
                open={showScheduleDialog}
                onClose={() => {
                    setShowScheduleDialog(false);
                    setScheduleData({ notificationDate: "", notificationTime: "" });
                }}
                onConfirm={handleScheduleSubmit}
                title="Schedule Shift Notification"
                description="Choose when you want the shift notification to be sent out"
                confirmLabel="Schedule Notification"
            >
                <DateTimeField
                    label="Notification Date"
                    icon={<CalendarMonth sx={{ height: 1, fontSize: 15, color: "gray" }} />}
                    type="date"
                    value={scheduleData.notificationDate}
                    onChange={(e) => {
                        setScheduleData(prev => ({ ...prev, notificationDate: e.target.value }));
                        clearError('notificationDate');
                        clearError('notificationDateTime');
                    }}
                    min={getMinDate()}
                    error={errors.notificationDate}
                />

                <DateTimeField
                    label={`Notification Time (${timeZoneShort})`}
                    icon={<AccessTime sx={{ height: 1, fontSize: 15, color: "gray" }} />}
                    type="time"
                    value={scheduleData.notificationTime}
                    onChange={(e) => {
                        setScheduleData(prev => ({ ...prev, notificationTime: e.target.value }));
                        clearError('notificationTime');
                        clearError('notificationDateTime');
                    }}
                    error={errors.notificationTime || errors.notificationDateTime}
                />
            </ConfirmationDialog>

            {/* Confirm Post Dialog */}
            <ConfirmationDialog
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={() => submitShift(false)}
                title="Confirm Shift Details"
                description="Please review the shift details before posting"
                confirmLabel="Post Now"
                isSubmitting={isSubmitting}
            >
                <ShiftDetailsDisplay details={getShiftDetails()} />
                <ConfirmationExtras directReportOnly={formData.directReportOnly} notes={formData.notes} />
            </ConfirmationDialog>

            {/* Confirm Schedule Dialog */}
            <ConfirmationDialog
                open={showScheduleConfirmDialog}
                onClose={() => {
                    setShowScheduleConfirmDialog(false);
                    setShowScheduleDialog(true);
                }}
                onConfirm={() => submitShift(true)}
                title="Confirm Scheduled Shift"
                description="Please review the shift and schedule details before confirming"
                confirmLabel="Confirm Schedule"
                isSubmitting={isSubmitting}
            >
                <ShiftDetailsDisplay details={getShiftDetails(false)} />
                <ConfirmationExtras directReportOnly={formData.directReportOnly} notes={formData.notes} />
                <div className={styles.scheduleBorder}>
                    <div className={styles.confirmSection}>
                        <p className={styles.confirmLabel}>Notification will be sent on:</p>
                        <p className={styles.confirmValue}>
                            {formatDate(scheduleData.notificationDate)} at {scheduleData.notificationTime}
                        </p>
                    </div>
                </div>
            </ConfirmationDialog>
        </div>
    );
};

export default PostShiftPage;